/**
 * The notice's READ module: it answers only WHETHER a notice is due, and
 * nothing about how one presents.
 *
 * The verdict is the one shared predicate `termsNoticeVerdict` from
 * `@lds-efsp/shared-contracts`, judged over the portal's bundled registry at
 * the instant this read pinned, reconciled against the API's reported leg by
 * {@link reconcileNoticeVerdict} — the reported leg wins when it is due, and
 * the bundled leg's own answer stands when the API is quiet. Neither half is
 * re-derived here, and this module orders no instant against another: every
 * selector it calls takes the caller's instant as an argument.
 *
 * Where the notice LEADS and WHAT it may say is a separate question with a
 * separate decider, and that decider is deliberately not named here — nor
 * anywhere else in this file. It lives with the registry, in
 * `packages/shared-contracts/src/features/legal/legal-registry.ts`: the
 * selector that asks the announced-standing question for the version the
 * verdict named and answers one of exactly two standing-derived pairs, which
 * the notice's composing hook in
 * `apps/user-portal/src/features/legal/useTermsNotice.ts` reads at the same
 * pinned instant this module's read pinned. Two reasons the presentation is
 * not decided here, and neither is tidiness. SPEC-SOR-421's shards
 * (`spec_sor_421proptest/gen/r3.ts`, `r9.ts`, `r10.ts`) text-scan this file
 * with verbatim pins — the one-member return, the two state declarations, the
 * literal projection, the gate and the failed-branch answer — so a presentation
 * decided in this module would widen a pinned surface; and the initiative's own
 * one-decider rule is swept across the whole portal source tree, so a second
 * file in the portal that names the decider is itself the defect that sweep
 * exists to catch.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import {
  termsNoticeVerdict,
  type LegalAcceptanceState,
  type TermsNoticeAccount,
  type TermsNoticeVerdict,
} from '@lds-efsp/shared-contracts';
import { useLegalApi } from '../../api/useLegalApi';
import { useAuthStatus } from '../../auth/useAuthStatus';

/**
 * The window a failed read re-arms its retry on. Declared once, here, with the
 * module that owns the read: the legal-state read is now the only consumer of
 * the endpoint, so the retry window lives with it and nothing else restates it.
 */
export const LEGAL_API_RETRY_MS = 30_000;

/**
 * The quiet verdict — no notice, both announced fields explicitly null. It is
 * the answer for every state that has nothing to announce: the read has not
 * landed yet, the read failed, or the account is not one the notice is ever
 * shown to. Modelling "no notice" as a named value with explicit nulls (rather
 * than an absent verdict) is what leaves a caller nothing to gate on.
 */
export const NO_LEGAL_NOTICE: TermsNoticeVerdict = Object.freeze({
  due: false,
  version: null,
  effectiveDate: null,
});

/**
 * One completed read of the legal state for the signed-in account: the state
 * the API answered, together with the comparison instant pinned WITH that read
 * — or the fact that the read failed. The instant rides the read because the
 * verdict is judged at it and at nothing else, so an accepted read carries one
 * instant that both halves are evaluated against.
 */
export type LegalNoticeRead =
  | { outcome: 'accepted'; state: LegalAcceptanceState; readAt: string }
  | { outcome: 'failed' };

/**
 * Reconcile the two halves of the notice verdict into the one the portal
 * renders: the API's reported `notice` leg, and this bundle's own evaluation
 * of the shared predicate over the bundled registry.
 *
 * The reported half wins whenever it is due; the bundled half answers only
 * when the API is quiet. That direction is not the announcement's, it is the
 * dismissal's: the notice is dismissed by posting the version it announced,
 * and the server records a dismissal only for the version its OWN evaluation
 * of the shared predicate stands on. A fold that let the bundled half win
 * whenever it was due would render — and post — a number the server refuses
 * for as long as the two registry copies disagree, which is exactly the deploy
 * window: the read re-fires on every route change and the fold is
 * deterministic, so the notice could never be put away.
 *
 * The bundled half keeps the one window where it is the only answer: when the
 * API's copy has nothing to announce — the portal deployed ahead of the API —
 * the bundled verdict is the only announcement there is, and showing it is how
 * a newly posted version reaches a customer before the API catches up. Nothing
 * is dismissable in that window either (the server has no standing version to
 * record against, so it refuses every body), so the notice waits out the
 * deploy rather than being suppressed by it. Both quiet → quiet, so the fold
 * introduces no notice of its own.
 *
 * Where the halves disagree the announcement follows the API's version while
 * the `/legal/terms` link still renders this bundle's text, so a customer may
 * be shown a version whose wording this build does not carry yet. That skew is
 * the honest one: the announced version is the version the account is held to.
 *
 * This is not a second comparison: neither half is re-derived here, the fold
 * only orders two verdicts the shared predicate already computed. It differs
 * from the predicate by fact domain — the predicate answers "what does this
 * account owe against this registry", the fold answers "what does the portal
 * render given two answers to that question".
 */
export function reconcileNoticeVerdict(
  reported: TermsNoticeVerdict,
  local: TermsNoticeVerdict,
): TermsNoticeVerdict {
  if (reported.due) return reported;
  return local.due ? local : reported;
}

/**
 * The verdict one read yields for one account class — the total pure decider
 * the hook's effect applies to every settled read.
 *
 * A failed read reports no notice at all (R9). That is visit-scoped, so it
 * clears a verdict an earlier accepted read had made due: a failed read is
 * answered by a quiet verdict, never by the last one that landed, and no error
 * survives into the return value for a caller to gate on.
 *
 * An accepted read builds the account the shared predicate reads — the class
 * the caller carries, the recorded accepted Terms version, the recorded
 * dismissal standing — and reconciles the reported leg with the predicate's
 * own answer for the bundled registry at the instant pinned with this read.
 * Platform accounts are answered here rather than by a branch at the call
 * site, so "operators and admins never receive a verdict" holds for the
 * decider itself and not only for the read gate.
 */
export function noticeVerdictForRead(
  read: LegalNoticeRead,
  isPlatformUser: boolean,
): TermsNoticeVerdict {
  if (isPlatformUser) return NO_LEGAL_NOTICE;
  if (read.outcome === 'failed') return NO_LEGAL_NOTICE;

  const account: TermsNoticeAccount = {
    isPlatformUser,
    acceptedTermsVersion: read.state.accepted.termsVersion,
    dismissedTermsVersion: read.state.dismissedNotice.version,
  };

  return reconcileNoticeVerdict(
    read.state.notice,
    termsNoticeVerdict(account, read.readAt),
  );
}

/**
 * Whether this session's legal state is read at all: the notice is a
 * customer's, so a signed-out session never fires a doomed fetch and a
 * platform user (operator or admin) never consults the endpoint.
 */
export function shouldReadLegalNoticeState(
  isAuthenticated: boolean,
  isPlatformUser: boolean,
): boolean {
  return isAuthenticated && !isPlatformUser;
}

/**
 * The legal notice state for the signed-in customer — the one module that
 * reads `GET /api/legal/acceptance`, so the notice surface renders a verdict
 * it did not have to fetch.
 *
 * Deliberate behaviors, carried over from the blocking interstitial's read
 * discipline (SOR-344) rather than reinvented:
 * - One read per route change. The effect keys on the router location's
 *   pathname, so a navigation fires exactly one read and a re-render that does
 *   not move the pathname fires none — no timer polling, and a version
 *   published mid-session is picked up within one navigation rather than only
 *   at the next sign-in.
 * - The comparison instant is pinned once per accepted read, with that read,
 *   and never on render: both halves of the verdict are judged at the one
 *   instant the read paid for.
 * - Fail-open and quiet. A failed read reports no notice and arms one retry on
 *   the 30 s window; the next route change fires a fresh read regardless of
 *   the pending retry (the navigation's cleanup clears it), and nothing fires
 *   after unmount.
 * - Nothing to gate on. The return value carries the verdict, and the verdict
 *   of a failed or not-yet-settled read is the quiet one — there is no error
 *   leg and no loading leg for a caller to branch the application on.
 */
export function useLegalNoticeState(): { verdict: TermsNoticeVerdict } {
  const location = useLocation();
  const { isAuthenticated, isPlatformUser } = useAuthStatus();
  const { getAcceptanceState } = useLegalApi();
  const [readVerdict, setReadVerdict] = useState<TermsNoticeVerdict>(NO_LEGAL_NOTICE);
  // Bumped by the retry timer to re-run the read effect — a retry that
  // re-enters through the dependency list rather than a self-referencing call.
  const [retryNonce, setRetryNonce] = useState(0);

  const enabled = shouldReadLegalNoticeState(isAuthenticated, isPlatformUser);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    getAcceptanceState()
      .then((next) => {
        if (cancelled) return;
        setReadVerdict(
          noticeVerdictForRead(
            { outcome: 'accepted', state: next, readAt: new Date().toISOString() },
            isPlatformUser,
          ),
        );
      })
      .catch(() => {
        if (cancelled) return;
        // Fail-quiet: a read that failed reports no notice, whatever an
        // earlier read had answered, and re-arms one retry.
        setReadVerdict(noticeVerdictForRead({ outcome: 'failed' }, isPlatformUser));
        retryTimer = setTimeout(() => {
          if (!cancelled) setRetryNonce((nonce) => nonce + 1);
        }, LEGAL_API_RETRY_MS);
      });
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [enabled, getAcceptanceState, retryNonce, location.pathname, isPlatformUser]);

  // A session the notice is not for carries no verdict at all, so a signed-out
  // or platform session can never render one left over from an earlier read.
  return { verdict: enabled ? readVerdict : NO_LEGAL_NOTICE };
}
